import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Highlighter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  FEEDBACK_TAGS,
  categoryForTag,
  kindForTag,
  tagNotePlaceholder,
  tagNoteRequired,
} from "@/lib/docFeedbackTags";

const BLOCK_SELECTOR = "p, li, td, h1, h2, h3, h4, h5, h6";

type Captured = {
  quote: string;
  quoteContext: string;
  paragraphIndex: number | null;
  range: Range;
  left: number;
  top: number;
};

export function SelectionFlagger({
  containerRef,
  docId,
  collection,
  docVersion,
  onFiled,
}: {
  containerRef: React.RefObject<HTMLElement>;
  docId: string;
  collection: "faq" | "sop";
  docVersion?: string;
  onFiled?: (category: "confusion" | "suggestion") => void;
}) {
  const [captured, setCaptured] = useState<Captured | null>(null);
  const [open, setOpen] = useState(false);
  const [tag, setTag] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const openRef = useRef(false);
  openRef.current = open;

  const reset = useCallback(() => {
    setCaptured(null);
    setOpen(false);
    setTag(null);
    setNote("");
    try {
      window.getSelection()?.removeAllRanges();
    } catch {}
  }, []);

  // Reset when the document changes.
  useEffect(() => {
    setCaptured(null);
    setOpen(false);
    setTag(null);
    setNote("");
  }, [docId]);

  useEffect(() => {
    const onMouseUp = () => {
      if (openRef.current) return;
      window.setTimeout(() => {
        const container = containerRef.current;
        const sel = window.getSelection();
        if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) {
          setCaptured(null);
          return;
        }
        const range = sel.getRangeAt(0);
        if (!container.contains(range.commonAncestorContainer)) {
          setCaptured(null);
          return;
        }
        const quote = sel.toString().trim();
        if (!quote) {
          setCaptured(null);
          return;
        }
        const startNode =
          range.startContainer.nodeType === Node.ELEMENT_NODE
            ? (range.startContainer as Element)
            : range.startContainer.parentElement;
        const block = startNode?.closest(BLOCK_SELECTOR) ?? null;
        const blocks = Array.from(container.querySelectorAll(BLOCK_SELECTOR));
        const paragraphIndex = block ? blocks.indexOf(block) : -1;
        const rects = range.getClientRects();
        const last = rects.length ? rects[rects.length - 1] : range.getBoundingClientRect();
        setCaptured({
          quote,
          quoteContext: block?.textContent?.trim() || quote,
          paragraphIndex: paragraphIndex >= 0 ? paragraphIndex : null,
          range: range.cloneRange(),
          left: Math.max(8, last.left),
          top: last.bottom + 6,
        });
      }, 0);
    };
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, [containerRef]);

  function flashHighlight(range: Range) {
    try {
      const mark = document.createElement("mark");
      mark.style.backgroundColor = "rgba(251, 191, 36, 0.35)";
      mark.appendChild(range.extractContents());
      range.insertNode(mark);
      window.setTimeout(() => {
        const parent = mark.parentNode;
        if (!parent) return;
        while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
        parent.normalize();
      }, 2000);
    } catch {
      // Selection spanned nodes we cannot wrap. Nothing to show, the flag still sent.
    }
  }

  const noteRequired = !!tag && tagNoteRequired(tag);
  const canSend = !!tag && (!noteRequired || note.trim().length > 0);

  async function send() {
    if (!captured || !tag || !canSend) return;
    setSending(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id ?? null;
      const category = categoryForTag(tag);
      const { error } = await supabase.from("doc_feedback" as any).insert({
        doc_id: docId,
        collection,
        doc_version: docVersion ?? null,
        user_id: uid,
        tag,
        quote: captured.quote,
        quote_context: captured.quoteContext,
        paragraph_index: captured.paragraphIndex,
        context: typeof window !== "undefined" ? window.location.pathname : null,
        note: note.trim() || null,
        category,
        kind: kindForTag(tag),
        suggested_term: tag === "DEFINE" ? captured.quote : null,
      } as any);
      if (error) throw error;
      toast.success(`Flagged: ${tag}`);
      onFiled?.(category);
      flashHighlight(captured.range);
      reset();
    } catch (e: any) {
      console.error("[selection flag] insert failed", e);
      toast.error("Could not send that flag. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (!captured) return null;

  const display =
    captured.quote.length > 240 ? `${captured.quote.slice(0, 240)}...` : captured.quote;

  return (
    <div
      className="fixed z-50"
      style={{ left: captured.left, top: captured.top }}
      onMouseUp={(e) => e.stopPropagation()}
    >
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="rounded-full bg-foreground text-background text-xs px-3 py-1.5 shadow-lg flex items-center gap-1.5"
          >
            <Highlighter className="h-3.5 w-3.5" /> Flag this
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[380px] space-y-3">
          <div className="rounded border bg-muted/40 px-2 py-1.5 text-xs text-muted-foreground max-h-24 overflow-auto">
            {display}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTag(t)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  tag === t
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background hover:bg-muted"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={tagNotePlaceholder(tag)}
            className="text-sm"
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={reset}>
              Cancel
            </Button>
            <Button size="sm" onClick={send} disabled={!canSend || sending}>
              {sending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Send
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
